import { Navigate } from "@/components/_molecules/navigation-controls/navigate";
import { NotFoundView } from "@/components/_molecules/not-found-view";
import { authRoutes } from "@/domain/_shared/auth/routes/auth.routes.tsx";
import { featureFlagSettingsRoutes } from "@/domain/_shared/feature-flag/routes/feature-flag.routes.tsx";
import { activityLogRoutes } from "@/domain/activity-log/routes/activity-log.routes.tsx";
import { climateActionRoutes, myClimateActionRoutes } from "@/domain/climate-action/routes/climate-action.routes.tsx";
import { dataRequestRoutes } from "@/domain/data-request/routes/data-request.routes.tsx";
import { focusAreaRoutes, focusAreaSettingsRoutes } from "@/domain/focus-area/routes/focus-area.routes.tsx";
import { indicatorRoutes } from "@/domain/indicator/routes/indicator.routes.tsx";
import { inventoryRoutes } from "@/domain/inventory/routes/inventory.routes.tsx";
import { labelSettingsRoutes } from "@/domain/label/routes/label.routes.tsx";
import { municipalPropertyRouteList } from "@/domain/municipal-property/routes/municipal-property.routes.tsx";
import { MunicipalPropertyLegacyRedirectPage } from "@/domain/municipal-property/routes/municipal-property-legacy-redirect.page";
import { offeringRoutes } from "@/domain/offering/routes/offering.routes.tsx";
import { phaseSettingsRoutes } from "@/domain/phase/routes/phase.routes.tsx";
import { prioritySettingsRoutes } from "@/domain/priority/routes/priority.routes.tsx";
import { myProjectRoutes, projectRoutes } from "@/domain/project/routes/project.routes.tsx";
import { publicDashboardRoutes } from "@/domain/public-dashboard/routes/public-dashboard.routes.tsx";
import { scenarioRoutes } from "@/domain/scenario/routes/scenario.routes.tsx";
import { statusSettingsRoutes } from "@/domain/status/routes/status.routes.tsx";
import { myTasksRoutes, taskRoutes } from "@/domain/task/routes/task.routes.tsx";
import {
  sharedTeamRoutes,
  teamRoutes,
  teamSettingsRoutes,
  workspaceTeamSettingsRoutes,
} from "@/domain/team/routes/team.routes.tsx";
import { userSettingsRoutes } from "@/domain/user/routes/user.routes.tsx";
import { workspaceAdminRoutes, workspaceSettingsRoutes } from "@/domain/workspace/routes/workspace.routes.tsx";

import { unitSettingsRoutes } from "../domain/unit/routes/unit.routes.tsx";

import { AppLayout } from "./app.layout.tsx";
import { lazyRoute } from "./lazy-route.tsx";
import { ProtectedRoute } from "./protected-route.tsx";
import { publicEmbedRoutes } from "./public-embed.routes.tsx";
import { SettingsRootLayout } from "./settings.layout.tsx";
import { WorkspaceLayout } from "./workspace.layout.tsx";
import { WorkspaceAdminRoute } from "./workspace-admin-route.tsx";

const AccessibilityStatementPage = lazyRoute(
  () => import("@/domain/_shared/accessibility/routes/accessibility-statement.page"),
  "AccessibilityStatementPage",
);

export const routes = (isAuthenticated: boolean) => [
  {
    path: "/",
    element: (
      <ProtectedRoute isAuthenticated={isAuthenticated}>
        <AppLayout />
      </ProtectedRoute>
    ),
  },

  ...authRoutes(isAuthenticated),
  ...publicEmbedRoutes,
  {
    // Exempt from auth by BITV § 7.
    path: "/accessibility",
    element: <AccessibilityStatementPage />,
  },

  {
    path: "/:workspaceUrl",
    element: (
      <ProtectedRoute isAuthenticated={isAuthenticated}>
        <WorkspaceLayout />
      </ProtectedRoute>
    ),
    children: [
      { path: "", element: <Navigate to="my-climate-actions" replace /> },

      ...myTasksRoutes,
      ...myProjectRoutes,
      ...myClimateActionRoutes,

      ...climateActionRoutes,
      ...projectRoutes,
      ...taskRoutes,
      ...focusAreaRoutes,
      ...indicatorRoutes,
      ...inventoryRoutes,
      ...scenarioRoutes,
      ...dataRequestRoutes,
      { path: "properties/*", element: <MunicipalPropertyLegacyRedirectPage /> },
      ...activityLogRoutes,
      ...offeringRoutes,

      ...teamRoutes,
      ...sharedTeamRoutes,
      ...publicDashboardRoutes,

      { path: "*", element: <NotFoundView /> },
    ],
  },

  {
    path: "/:workspaceUrl/settings",
    element: (
      <ProtectedRoute isAuthenticated={isAuthenticated}>
        <SettingsRootLayout />
      </ProtectedRoute>
    ),
    children: [
      ...userSettingsRoutes,
      ...featureFlagSettingsRoutes,
      ...municipalPropertyRouteList,

      {
        path: "workspace",
        element: <WorkspaceAdminRoute />,
        children: [
          ...workspaceSettingsRoutes,
          ...focusAreaSettingsRoutes,
          ...labelSettingsRoutes,
          ...phaseSettingsRoutes,
          ...statusSettingsRoutes,
          ...prioritySettingsRoutes,
          ...unitSettingsRoutes,
          ...workspaceTeamSettingsRoutes,
        ],
      },

      ...teamSettingsRoutes,

      { path: "*", element: <NotFoundView /> },
    ],
  },

  ...workspaceAdminRoutes(isAuthenticated),

  { path: "*", element: <NotFoundView /> },
];
